import {
    faBan,
    faBorderAll,
    faCheck,
    faDollarSign,
    faEdit,
    faHashtag,
    faLink,
    faPersonChalkboard,
    faPlay,
    faPlus,
    faRedo,
    faSync,
    faTimes,
    faTrash,
    faUserCheck,
    faUserXmark
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useApi } from '@hooks/useApi';
import { useConfirmedCallback } from '@hooks/useConfirmedCallback';
import { useModal } from '@hooks/useModal';
import DropdownItem from '@ui/Dropdown/DropdownItem';
import moment from 'moment/moment';
import dynamic from 'next/dynamic';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { useSWRConfig } from 'swr';
import Loader from '../../../Loader';
import { useClubSlug } from '../../../Clubs/hooks';
import AllocationList from '../../AllocationList';
import AllocationStatusBadge from '../../AllocationStatusBadge';
import AttendanceBadge from '../../Attendance/AttendanceBadge';
import AttendanceBookerToggle from '../../Attendance/AttendanceBookerToggle';
import useAttendanceConfirmation from '../../Attendance/useAttendanceConfirmation';
import BookerStatusBadge from '../../BookerStatusBadge';
import { Allocation } from '../../types';

const AllocationFormWithSchedule = dynamic(() => import('../../AllocationForm/AllocationFormWithSchedule'));
const AllocationCancellationForm = dynamic(() => import('../../AllocationCancellationForm'));
const AllocationRebookingLinkForm = dynamic(() => import('../../AllocationList/AllocationRebookingLinkForm'));
const AllocationRequestResponseForm = dynamic(
    () => import('../../AllocationRequestList/AllocationRequestResponseForm')
);

interface Props {
    allocation: Required<Pick<Allocation, 'id'>> & Partial<Allocation>;
}

const AllocationPreview: FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({
    allocation: _allocation
}) => {
    const { t } = useTranslation('space-manager');
    const api = useApi();
    const clubSlug = useClubSlug();
    const { mutate } = useSWRConfig();
    const [isLoading, setIsLoading] = useState(false);
    const [setModal, resetModal] = useModal();
    const {
        confirm: confirmAttendance,
        isLoading: attendanceIsLoading,
        canConfirm: canConfirmAttendance
    } = useAttendanceConfirmation();
    const { data: allocationData, isLoading: allocationIsLoading } = useSWR<{
        data: Allocation | typeof _allocation;
    }>(_allocation?.id ? `space-manager/allocation/${_allocation.id}` : null, null, {
        fallback: { data: _allocation }
    });
    const allocation = allocationData?.data;

    const refetch = useCallback(() => {
        void mutate(`space-manager/allocation/${_allocation.id}`);
        void mutate((key: string) => key.includes(`/space-manager/space/grid`), undefined, { revalidate: true });
    }, []);

    const handleOpenAllocationForm = useCallback(() => {
        setModal({
            title: t('views.allocationPreview.editAllocation'),
            content: (
                <AllocationFormWithSchedule
                    allocation={allocation}
                    callback={() => {
                        refetch();
                        resetModal();
                    }}
                />
            ),
            show: true,
            size: 85
        });
    }, [allocation, t]);

    const handleCancel = () => {
        setModal({
            title: t('views.allocationPreview.cancelAllocation'),
            content: (
                <AllocationCancellationForm
                    allocation={allocation}
                    callback={() => {
                        refetch();
                        resetModal();
                    }}
                />
            ),
            show: true
        });
    };

    const handleCreateRebookingLink = useCallback(async () => {
        setModal({
            title: t('views.allocationPreview.createRebookingLink'),
            content: (
                <AllocationRebookingLinkForm
                    allocation={allocation as any}
                    callback={async (data) => {
                        resetModal();
                        const fallbackMessage = t('views.allocationPreview.rebookingLinkCreatedFallback', {
                            link: data.link
                        });
                        if (!navigator.clipboard) {
                            alert(fallbackMessage);
                            return;
                        }
                        try {
                            await navigator.clipboard.writeText(data.link);
                            alert(t('views.allocationPreview.rebookingLinkCreatedSuccess'));
                        } catch {
                            alert(fallbackMessage);
                        }
                    }}
                />
            ),
            show: true,
            size: 40
        });
    }, [allocation, setModal, resetModal, t]);

    // an open request still awaits a decision — it drives both the extra and the hidden actions
    const isPendingRequest = allocation?.status === 'pending';

    // accept/decline an open booking request without leaving the grid
    const handleRespondToRequest = useCallback(
        (action: 'accept' | 'decline') => {
            setModal({
                title:
                    action === 'accept'
                        ? t('allocationRequestList.modals.accept', { id: allocation?.id })
                        : t('allocationRequestList.modals.decline', { id: allocation?.id }),
                content: (
                    <AllocationRequestResponseForm
                        action={action}
                        allocation={allocation as any}
                        clubSlug={clubSlug}
                        callback={() => {
                            resetModal();
                            refetch();
                            void mutate(`club/${clubSlug}/space-manager/allocation/requests/count`);
                        }}
                    />
                ),
                show: true,
                size: 40
            });
        },
        [allocation, clubSlug, refetch, setModal, resetModal, mutate, t]
    );

    // confirms or withdraws the attendance; without a booker id it acts on the whole booking.
    // Deliberately without a confirmation dialog — the preview already lives in a modal and the
    // action is a toggle that can be taken back right away.
    const handleToggleAttendance = (verified: boolean, bookerId?: number) =>
        void confirmAttendance({ allocationId: allocation?.id, bookerId, verified }, refetch);

    const handleShowRecurringItems = () => {
        setModal({
            title: t('views.allocationPreview.recurringDates'),
            content: (
                <div className="tw-flex tw-h-[60vh] tw-flex-col">
                    <AllocationList recurringId={allocation.recurring_id} />
                </div>
            ),
            show: true
        });
    };

    const handleDelete = useConfirmedCallback(() => {
        setIsLoading(true);
        void api({
            url: `space-manager/allocation/${allocation.id}`,
            method: 'DELETE',
            onSuccess: () => refetch()
        });
    });

    return (
        <Loader isLoading={allocationIsLoading}>
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
                <h3>{allocation?.title}</h3>
                <span>{allocation?.recurring_id && <FontAwesomeIcon icon={faRedo} />}</span>
            </div>
            {/* anything but a plain confirmed booking gets a badge — booking requests must not
                look like regular bookings, they are not binding yet */}
            {!!allocation?.status && allocation.status !== 'active' && (
                <div className="tw-mb-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <AllocationStatusBadge status={allocation.status} />
                    {allocation.status === 'awaiting_payment' && !!allocation.payment_due_at && (
                        <span className="tw-text-xs tw-text-accents-6">
                            {t('views.allocationPreview.payUntil', {
                                date: moment(allocation.payment_due_at).format('DD.MM.YY HH:mm')
                            })}
                        </span>
                    )}
                    {allocation.status === 'pending' && (
                        <span className="tw-text-xs tw-text-accents-6">
                            {t('views.allocationPreview.pendingHint')}
                        </span>
                    )}
                </div>
            )}
            {/* only shown when the VerifyAttendance effect applies to this booking */}
            {!!allocation?.attendance && (
                <div className="tw-mb-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <AttendanceBadge attendance={allocation.attendance} />
                </div>
            )}
            <div className="tw-flex tw-items-center tw-text-xs">
                <div className="tw-mr-4 tw-flex-1 tw-border-0 tw-border-r tw-border-solid tw-border-accents-2 tw-pr-4 tw-text-accents-9">
                    <div className="tw-my-2 tw-flex tw-flex-col tw-text-accents-6">
                        <span className="tw-flex tw-items-center tw-gap-2">
                            <FontAwesomeIcon icon={faHashtag} />
                            <span>{allocation?.id}</span>
                        </span>
                        <span className="tw-flex tw-items-center tw-gap-2">
                            <FontAwesomeIcon icon={faBorderAll} />
                            <span>{allocation?.space?.name}</span>
                        </span>
                        <span className="tw-flex tw-items-center tw-gap-2">
                            <FontAwesomeIcon icon={faPlay} />
                            <span>{`${moment(allocation?.start).format('DD.MM.YY HH:mm')} - ${moment(allocation?.end).format('HH:mm')}`}</span>
                        </span>
                        <span className="tw-flex tw-items-center tw-gap-2">
                            <FontAwesomeIcon icon={faPlus} />
                            <span>{allocation?.created_at ? moment(allocation.created_at).format('DD.MM.YY HH:mm') : '-'}</span>
                        </span>
                        {allocation?.created_at && allocation?.updated_at && allocation.created_at !== allocation.updated_at && (
                            <span className="tw-flex tw-items-center tw-gap-2">
                                <FontAwesomeIcon icon={faSync} />
                                <span>{moment(allocation.updated_at).format('DD.MM.YY HH:mm')}</span>
                            </span>
                        )}
                        {allocation?.trainer && (
                            <span className="tw-flex tw-items-center tw-gap-2">
                                <FontAwesomeIcon icon={faPersonChalkboard} />
                                <span>{allocation?.trainer?.name}</span>
                            </span>
                        )}
                        <span className="tw-flex tw-items-center tw-gap-2">
                            <FontAwesomeIcon icon={faDollarSign} />
                            <span>{allocation?.stats?.price_total ?? '-'} €</span>
                        </span>
                    </div>
                </div>
                <div className="tw-flex tw-flex-1 tw-flex-col tw-text-accents-6">
                    {allocation?.bookers?.length > 0 ? (
                        allocation.bookers.map((booker) => (
                            <div className="tw-flex tw-items-center tw-gap-2" key={booker.id}>
                                <BookerStatusBadge booker={booker} />
                                {/* per-booker confirmation — the whole booking is handled by the
                                    action below; read-only without the approve permission */}
                                {!!allocation?.attendance && (
                                    <AttendanceBookerToggle
                                        booker={booker}
                                        disabled={attendanceIsLoading}
                                        onToggle={
                                            canConfirmAttendance
                                                ? (verified) => handleToggleAttendance(verified, booker.id)
                                                : undefined
                                        }
                                    />
                                )}
                                <span>{booker.user_name}</span>
                            </div>
                        ))
                    ) : (
                        <span className="tw-text-xs tw-italic tw-accent-accents-6">
                            {t('views.allocationPreview.noBookers')}
                        </span>
                    )}
                </div>
            </div>
            {allocation?.remarks && (
                <div className="tw-my-4">
                    <span className="tw-text-sm tw-italic tw-text-accents-6">{allocation.remarks}</span>
                </div>
            )}
            <div className="tw-mt-4 tw-grid tw-grid-cols-2 tw-justify-between">
                {isPendingRequest && (
                    <>
                        <DropdownItem
                            label={t('views.allocationPreview.actions.acceptRequest')}
                            icon={<FontAwesomeIcon icon={faCheck} />}
                            color="success"
                            disabled={isLoading}
                            onClick={() => handleRespondToRequest('accept')}
                        />
                        <DropdownItem
                            label={t('views.allocationPreview.actions.declineRequest')}
                            icon={<FontAwesomeIcon icon={faTimes} />}
                            color="error"
                            disabled={isLoading}
                            onClick={() => handleRespondToRequest('decline')}
                        />
                    </>
                )}
                {/* confirms every booker at once — also revives a booking the prune job already
                    cancelled, as long as nobody took the slot in the meantime */}
                {!!allocation?.attendance && canConfirmAttendance && (
                    <DropdownItem
                        label={
                            allocation.attendance.fulfilled
                                ? t('views.allocationPreview.actions.withdrawAttendance')
                                : t('views.allocationPreview.actions.confirmAttendance')
                        }
                        icon={
                            <FontAwesomeIcon icon={allocation.attendance.fulfilled ? faUserXmark : faUserCheck} />
                        }
                        color={allocation.attendance.fulfilled ? 'warning' : 'success'}
                        disabled={isLoading || attendanceIsLoading}
                        onClick={() => handleToggleAttendance(!allocation.attendance.fulfilled)}
                    />
                )}
                <DropdownItem
                    label={t('views.allocationPreview.actions.edit')}
                    icon={<FontAwesomeIcon icon={faEdit} />}
                    color="warning"
                    disabled={isLoading}
                    onClick={handleOpenAllocationForm}
                />
                {allocation?.recurring_id && (
                    <DropdownItem
                        label={t('views.allocationPreview.actions.editRecurring')}
                        icon={<FontAwesomeIcon icon={faEdit} />}
                        color="warning"
                        disabled={isLoading}
                        onClick={handleShowRecurringItems}
                    />
                )}
                {/* an open request is not a booking yet: there is nothing to rebook and nothing
                    to cancel — it is decided via accept/decline instead */}
                {!isPendingRequest && (
                    <>
                        <DropdownItem
                            label={t('views.allocationPreview.actions.createRebookingLink')}
                            icon={<FontAwesomeIcon icon={faLink} />}
                            disabled={isLoading}
                            onClick={handleCreateRebookingLink}
                        />
                        <DropdownItem
                            label={t('views.allocationPreview.actions.cancel')}
                            icon={<FontAwesomeIcon icon={faBan} />}
                            color="error"
                            disabled={isLoading}
                            onClick={handleCancel}
                        />
                    </>
                )}
                <DropdownItem
                    label={t('views.allocationPreview.actions.delete')}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    color="error"
                    disabled={isLoading}
                    onClick={handleDelete}
                />
            </div>
        </Loader>
    );
};

export default AllocationPreview;
